import { PageTile } from '@make-software/cspr-design';

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { NFTTokenMetadataEntry } from '@block-explorer/entities';
import { findMediaPreview } from '../../../utils/nft-token-metadata';
import { NftPreviewImage } from './NftPreviewImage';
import { NftPreviewMedia } from './NftPreviewMedia';
import {
  EmptyMediaPlaceholder,
  LoadingMediaPlaceholder,
} from './NftMediaPlaceholder';
import { getImageProxyUrl } from '../../../utils/cache-asset';

const StyledPageTile = styled(PageTile)(({ theme }) =>
  theme.withMedia({
    padding: 16,
    minWidth: ['auto', 'auto', 376],
    height: [256, 256, 'auto'],
    minHeight: 256,
  })
);

const deriveMediaType = async (url: string | undefined) => {
  if (url) {
    const response = await fetch(url, { method: 'HEAD' });
    const type = response.headers.get('Content-Type');

    return {
      type: type,
    };
  }

  return {
    type: '',
  };
};

export const NftTokenCard = ({
  nftTokenMetadataWithLinks,
}: {
  nftTokenMetadataWithLinks: NFTTokenMetadataEntry[] | null;
}) => {
  const RETINA_SCALE = 2;
  const IMAGE_WIDTH = 376;
  const CACHE_TTL = '2592000';

  const [mediaType, setMediaType] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const preview = nftTokenMetadataWithLinks?.find(findMediaPreview);
  const nftName = nftTokenMetadataWithLinks?.find(
    (metadata) => metadata.key === 'name'
  );
  const nftDescription = nftTokenMetadataWithLinks?.find(
    (metadata) => metadata.key === 'description'
  );
  const cachedUrl = getImageProxyUrl(preview?.value, {
    ttl: CACHE_TTL,
    width: IMAGE_WIDTH * RETINA_SCALE,
  });
  const cachedVideoUrl = getImageProxyUrl(preview?.value, {
    ttl: CACHE_TTL,
    width: '',
  });

  useEffect(() => {
    setLoading(true);
    deriveMediaType(cachedUrl)
      .then((media) => setMediaType(media.type))
      .finally(() => setLoading(false));
  }, [preview]);

  if (loading) {
    return <LoadingMediaPlaceholder />;
  }

  if (preview && mediaType) {
    if (mediaType.includes('image')) {
      return (
        <NftPreviewImage
          url={cachedUrl}
          nftAlt={`NFT asset - ${nftDescription?.value || nftName?.value || 'image'}`}
        />
      );
    } else if (mediaType.includes('video') || mediaType.includes('audio')) {
      return (
        <NftPreviewMedia
          url={cachedVideoUrl}
          mediaType={mediaType}
          nftAlt={`NFT asset - ${nftDescription?.value || nftName?.value || 'image'}`}
        />
      );
    }
  }

  return <EmptyMediaPlaceholder />;
};

export const NftTokenCardWithPageTile = (props: {
  nftTokenMetadataWithLinks: NFTTokenMetadataEntry[] | null;
}) => (
  <StyledPageTile>
    <NftTokenCard {...props} />
  </StyledPageTile>
);
